import { LiteracyScreeningGradesModel } from './LiteracyScreeningGradesModel';
import { LiteracyScreeningSchoolModel } from './LiteracyScreeningSchoolModel';
import { LiteracyScreeningScreenerModel } from './LiteracyScreeningScreenerModel';

export const LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION = 'BeginningOfYear';
export const LITERACY_SCREENING_MIDDLE_OF_YEAR_SESSION = 'MiddleOfYear';
export const LITERACY_SCREENING_END_OF_YEAR_SESSION = 'EndOfYear';

export const LITERACY_SCREENING_SESSIONS = [
  LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION,
  LITERACY_SCREENING_MIDDLE_OF_YEAR_SESSION,
  LITERACY_SCREENING_END_OF_YEAR_SESSION,
] as const;

export interface LiteracyScreeningSessionSaveModel {
  screenerStartDate: string | null;
  screenerEndDate: string | null;
  studentsAssessed: number | null;
  studentsBelowGradeLevel: number | null;
  studentIntervention: number | null;
  session: string;
}

export interface LiteracyScreeningGradeSaveModel {
  gradeLevel: string;
  screener: string;
  screenerSession: LiteracyScreeningSessionSaveModel[];
}

export interface LiteracyScreeningModel {
  id: string;
  schoolYear: string;
  leaIdentifier: string;
  schoolIdentifier: string;
  schoolName: string;
  grades: LiteracyScreeningGradeSaveModel[];
  priorEndOfYearStatus: string;
  beginningOfYearStatus: string;
  middleOfYearStatus: string;
  endOfYearStatus: string;
  beginningOfYearNotificationDate: string;
  middleOfYearNotificationDate: string;
  endOfYearNotificationDate: string;
  updatedBy: string;
  updatedDateTime: string;
}

export interface LiteracyScreeningBoyRowSaveInput {
  gradeLevel: string;
  screener: string;
  screenerStartDate: string;
  screenerEndDate: string;
  studentsAssessed: string;
  studentsBelowGradeLevel: string;
  studentIntervention: string;
  isTotal?: boolean;
}

export interface LiteracyScreeningSessionRowsInput {
  beginningOfYear?: LiteracyScreeningBoyRowSaveInput[];
  middleOfYear?: LiteracyScreeningBoyRowSaveInput[];
  endOfYear?: LiteracyScreeningBoyRowSaveInput[];
}

export interface LiteracyScreeningRouteIdentifiers {
  id?: string;
  schoolYear?: string;
  leaIdentifier?: string;
  schoolIdentifier?: string;
}

export interface LiteracyScreeningNotificationDatesInput {
  beginningOfYearNotificationDate?: string;
  middleOfYearNotificationDate?: string;
  endOfYearNotificationDate?: string;
}

const NUMERIC_COUNT_REGEX = /^\d+$/;

const parseWholeNumber = (
  value: string | number | null | undefined
): number => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return 0;
  }

  const trimmed = String(value).trim();
  if (!NUMERIC_COUNT_REGEX.test(trimmed)) {
    return 0;
  }

  return Number(trimmed);
};

const isMatchingSession = (
  session: string | undefined,
  sessionName: string
): boolean => (session || '').toLowerCase() === sessionName.toLowerCase();

const isBeginningOfYearSession = (session: string | undefined): boolean =>
  isMatchingSession(session, LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION);

const hasSessionDates = (session: {
  screenerStartDate?: string | null;
  screenerEndDate?: string | null;
}): boolean =>
  Boolean(session.screenerStartDate?.trim()) &&
  Boolean(session.screenerEndDate?.trim());

const hasNonTotalRows = (
  rows: LiteracyScreeningBoyRowSaveInput[] | undefined
): boolean => (rows || []).some((row) => !row.isTotal);

const hasRowsWithSessionDates = (
  rows: LiteracyScreeningBoyRowSaveInput[] | undefined
): boolean => (rows || []).some((row) => !row.isTotal && hasSessionDates(row));

const toEmptySessionSaveModel = (
  sessionName: string
): LiteracyScreeningSessionSaveModel => ({
  screenerStartDate: null,
  screenerEndDate: null,
  studentsAssessed: null,
  studentsBelowGradeLevel: null,
  studentIntervention: null,
  session: sessionName,
});

const toSessionSaveModel = (
  session: LiteracyScreeningScreenerModel | LiteracyScreeningSessionSaveModel
): LiteracyScreeningSessionSaveModel => {
  if (!hasSessionDates(session)) {
    return toEmptySessionSaveModel(session.session || '');
  }

  return {
    screenerStartDate: session.screenerStartDate || null,
    screenerEndDate: session.screenerEndDate || null,
    studentsAssessed: parseWholeNumber(session.studentsAssessed),
    studentsBelowGradeLevel: parseWholeNumber(session.studentsBelowGradeLevel),
    studentIntervention: parseWholeNumber(session.studentIntervention),
    session: session.session || '',
  };
};

const toGradeScreenerSessions = (
  sessions:
    | Array<LiteracyScreeningScreenerModel | LiteracyScreeningSessionSaveModel>
    | undefined
): LiteracyScreeningSessionSaveModel[] =>
  LITERACY_SCREENING_SESSIONS.map((sessionName) => {
    const match = (sessions || []).find((session) =>
      isMatchingSession(session.session, sessionName)
    );

    return match
      ? toSessionSaveModel(match)
      : toEmptySessionSaveModel(sessionName);
  });

export const ensureLiteracyScreeningSessionsForSave = (
  sessions:
    | Array<LiteracyScreeningScreenerModel | LiteracyScreeningSessionSaveModel>
    | undefined
): LiteracyScreeningSessionSaveModel[] => toGradeScreenerSessions(sessions);

export const isNewLiteracyScreeningId = (id?: string): boolean =>
  !id || id.trim() === '' || id === 'new';

export const applySessionRowsToGrades = (
  grades: LiteracyScreeningGradesModel[] | undefined,
  rows: LiteracyScreeningBoyRowSaveInput[],
  sessionName: string
): LiteracyScreeningGradesModel[] => {
  const originalGrades = grades || [];
  const updateGradeScreener = isBeginningOfYearSession(sessionName);

  return rows
    .filter((row) => !row.isTotal)
    .map((row) => {
      const existing = originalGrades.find(
        (grade) => grade.gradeLevel === row.gradeLevel
      );
      const otherSessions = (existing?.screenerSession || []).filter(
        (session) => !isMatchingSession(session.session, sessionName)
      );

      const screener = updateGradeScreener
        ? row.screener || existing?.screener || ''
        : existing?.screener || row.screener || '';

      const nextSession: LiteracyScreeningScreenerModel = {
        gradeLevel: row.gradeLevel,
        screener: row.screener || '',
        screenerStartDate: row.screenerStartDate || '',
        screenerEndDate: row.screenerEndDate || '',
        studentsAssessed: parseWholeNumber(row.studentsAssessed),
        studentsBelowGradeLevel: parseWholeNumber(row.studentsBelowGradeLevel),
        studentIntervention: parseWholeNumber(row.studentIntervention),
        session: sessionName,
      };

      if (!hasSessionDates(row)) {
        return {
          gradeLevel: row.gradeLevel,
          screener,
          screenerSession: otherSessions,
        };
      }

      return {
        gradeLevel: row.gradeLevel,
        screener,
        screenerSession: [...otherSessions, nextSession],
      };
    });
};

export const applyBoyRowsToGrades = (
  grades: LiteracyScreeningGradesModel[] | undefined,
  rows: LiteracyScreeningBoyRowSaveInput[]
): LiteracyScreeningGradesModel[] =>
  applySessionRowsToGrades(
    grades,
    rows,
    LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION
  );

export const applyLiteracyScreeningSessionRowsToGrades = (
  grades: LiteracyScreeningGradesModel[] | undefined,
  sessionRows: LiteracyScreeningSessionRowsInput
): LiteracyScreeningGradesModel[] => {
  const sessions: Array<
    [LiteracyScreeningBoyRowSaveInput[] | undefined, string]
  > = [
    [sessionRows.beginningOfYear, LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION],
    [sessionRows.middleOfYear, LITERACY_SCREENING_MIDDLE_OF_YEAR_SESSION],
    [sessionRows.endOfYear, LITERACY_SCREENING_END_OF_YEAR_SESSION],
  ];

  return sessions.reduce((nextGrades, [rows, sessionName]) => {
    if (!hasNonTotalRows(rows) || !hasRowsWithSessionDates(rows)) {
      return nextGrades;
    }

    return applySessionRowsToGrades(nextGrades, rows || [], sessionName);
  }, grades || []);
};

export const buildLiteracyScreeningSaveModel = (
  school: LiteracyScreeningSchoolModel | null,
  rows: LiteracyScreeningBoyRowSaveInput[],
  route: LiteracyScreeningRouteIdentifiers,
  laterSessionRows?: Pick<
    LiteracyScreeningSessionRowsInput,
    'middleOfYear' | 'endOfYear'
  >,
  notificationDates?: LiteracyScreeningNotificationDatesInput
): LiteracyScreeningModel => {
  const nextGrades = applyLiteracyScreeningSessionRowsToGrades(school?.grades, {
    beginningOfYear: rows,
    middleOfYear: laterSessionRows?.middleOfYear,
    endOfYear: laterSessionRows?.endOfYear,
  });

  return {
    id: isNewLiteracyScreeningId(route.id) ? '' : route.id || school?.id || '',
    schoolYear: String(route.schoolYear || school?.schoolYear || ''),
    leaIdentifier: route.leaIdentifier || school?.leaIdentifier || '',
    schoolIdentifier: route.schoolIdentifier || school?.schoolIdentifier || '',
    schoolName: school?.schoolName || '',
    grades: nextGrades.map((grade) => ({
      gradeLevel: grade.gradeLevel,
      screener: grade.screener || '',
      screenerSession: toGradeScreenerSessions(grade.screenerSession),
    })),
    priorEndOfYearStatus: school?.priorEndOfYearStatus || '',
    beginningOfYearStatus: school?.beginningOfYearStatus || '',
    middleOfYearStatus: school?.middleOfYearStatus || '',
    endOfYearStatus: school?.endOfYearStatus || '',
    beginningOfYearNotificationDate:
      notificationDates?.beginningOfYearNotificationDate ??
      school?.beginningOfYearNotificationDate ??
      '',
    middleOfYearNotificationDate:
      notificationDates?.middleOfYearNotificationDate ??
      school?.middleOfYearNotificationDate ??
      '',
    endOfYearNotificationDate:
      notificationDates?.endOfYearNotificationDate ??
      school?.endOfYearNotificationDate ??
      '',
    updatedBy: school?.updatedBy || '',
    updatedDateTime: school?.updatedDateTime || '',
  };
};
